import { CalendarOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText, formatDate } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  AssignationWeekRow,
  ListResponse,
  MeetingTimeRow,
  NotificationsResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";

const API_DATE = "YYYY-MM-DD";

interface MeetingTimeFormValues {
  valid_from: dayjs.Dayjs;
  week_day: number;
  day_time: string;
}

/**
 * Sidebar entry "congregation_times" → /congregation/congregation_times.
 *
 * When and on which day the congregation meets. A time is valid from its week
 * on until the next entry starts, so the list is a history rather than a set of
 * independent rows — which is why the server stores only the start.
 *
 * Applying a time is a separate step from saving it: the weeks already planned
 * keep their own day and hour until they are overwritten, either all at once
 * ("apply to the whole period") or week by week in the drawer.
 */
export function MeetingTimesPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<MeetingTimeFormValues>();

  const [editing, setEditing] = useState<MeetingTimeRow | "new" | null>(null);
  const [weeksOf, setWeeksOf] = useState<MeetingTimeRow | null>(null);

  const meetingTimes = useQuery({
    queryKey: ["congregation", "meeting_times"],
    queryFn: () => apiFetch<ListResponse<MeetingTimeRow>>("/congregation/meeting_times"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["congregation", "meeting_times"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: (values: MeetingTimeFormValues) => {
      const body = {
        // The server snaps this to the beginning of the week; sending the exact
        // day picked keeps that decision in one place.
        valid_from: values.valid_from.format(API_DATE),
        week_day: values.week_day,
        day_time: values.day_time,
      };

      return editing === "new" || editing === null
        ? apiFetch<NotificationsResponse>("/congregation/meeting_times", { method: "POST", body })
        : apiFetch<NotificationsResponse>(`/congregation/meeting_times/${editing.id}`, {
            method: "PUT",
            body,
          });
    },
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/congregation/meeting_times/${id}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const applyToPeriod = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/congregation/meeting_times/${id}/assign`, {
        method: "PUT",
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      // The weeks in the drawer are exactly what this rewrote.
      void queryClient.invalidateQueries({ queryKey: ["congregation", "assignation_times"] });
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: MeetingTimeRow | "new") => {
      setEditing(row);
      // resetFields before filling. The store no longer drops its values when
      // the dialog closes, so a field this handler does not write would keep
      // the previously edited row's value.
      form.resetFields();
      if (row !== "new") {
        form.setFieldsValue({
          valid_from: row.valid_from ? dayjs(row.valid_from) : undefined,
          week_day: row.week_day ?? undefined,
          day_time: row.day_time ?? "",
        } as MeetingTimeFormValues);
      }
    },
    [form],
  );

  const askAndRemove = useCallback(
    (row: MeetingTimeRow) => {
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("dialog.confirm_delete", {
          name: [formatDate(row.valid_from), row.week_day_label, row.day_time]
            .filter(Boolean)
            .join(" · "),
        }),
        okText: t("dialog.confirm"),
        okButtonProps: { danger: true },
        cancelText: t("dialog.cancel"),
        onOk: () => remove.mutate(row.id),
      });
    },
    [modal, t, remove],
  );

  const askAndApply = useCallback(
    (row: MeetingTimeRow) => {
      // This overwrites the day and hour of every planned week in the period,
      // so it asks first.
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: t("congregation_meeting_time.assign_all"),
        okText: t("dialog.confirm"),
        cancelText: t("dialog.cancel"),
        onOk: () => applyToPeriod.mutate(row.id),
      });
    },
    [modal, t, applyToPeriod],
  );

  const busy = save.isPending || remove.isPending || applyToPeriod.isPending;

  const columns = useMemo<TableProps<MeetingTimeRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.congregation_meeting_time.valid_from"),
        dataIndex: "valid_from",
        width: 160,
        defaultSortOrder: "descend",
        sorter: (a, b) => compareText(a.valid_from, b.valid_from),
        render: (value: string | null) => formatDate(value),
      },
      {
        title: t("activerecord.attributes.congregation_meeting_time.week_day"),
        dataIndex: "week_day_label",
        width: 160,
      },
      {
        title: t("activerecord.attributes.congregation_meeting_time.day_time"),
        dataIndex: "day_time",
        width: 120,
      },
      {
        key: "actions",
        fixed: "right",
        width: 190,
        render: (_value, row) => (
          <Space>
            <Tooltip title={t("form.update")}>
              <Button size="small" icon={<EditOutlined />} disabled={busy} onClick={() => openEditor(row)} />
            </Tooltip>
            <Tooltip title={t("congregation_meeting_time.assign_all")}>
              <Button
                size="small"
                icon={<CalendarOutlined />}
                disabled={busy}
                onClick={() => askAndApply(row)}
              />
            </Tooltip>
            <Tooltip title={t("congregation_meeting_time.remove")}>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={busy}
                onClick={() => askAndRemove(row)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [t, busy, openEditor, askAndApply, askAndRemove],
  );

  // 0 = Sunday, the order Rails' date.day_names uses and the server expects.
  const dayOptions = useMemo(() => {
    const names = t("date.day_names", { returnObjects: true }) as unknown;
    if (!Array.isArray(names)) return [];
    return names.map((label, value) => ({ value, label: String(label) }));
  }, [t]);

  return (
    <Card
      title={t("activerecord.models.congregation_meeting_time")}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor("new")}>
          {t("congregation_meeting_time.create")}
        </Button>
      }
    >
      <DataTable<MeetingTimeRow>
        columns={columns}
        rows={meetingTimes.data?.data}
        exportAs={t("activerecord.models.congregation_meeting_time")}
        loading={meetingTimes.isPending || busy}
        error={meetingTimes.error}
        onRowClick={setWeeksOf}
        paginated={false}
      />

      <Modal
        open={editing !== null}
        title={
          editing === "new"
            ? t("congregation_meeting_time.create")
            : t("activerecord.models.congregation_meeting_time")
        }
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<MeetingTimeFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate(values)}
        >
          <Form.Item
            name="valid_from"
            label={t("activerecord.attributes.congregation_meeting_time.valid_from")}
            rules={[{ required: true }]}
          >
            <DatePicker />
          </Form.Item>

          <Form.Item
            name="week_day"
            label={t("activerecord.attributes.congregation_meeting_time.week_day")}
            rules={[{ required: true }]}
          >
            <Select options={dayOptions} style={{ width: 200 }} />
          </Form.Item>

          <Form.Item
            name="day_time"
            label={t("activerecord.attributes.congregation_meeting_time.day_time")}
            rules={[
              { required: true },
              {
                // The same rule the model enforces; failing here saves a round
                // trip, and the server still has the last word.
                pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
                message: t(
                  "activerecord.errors.models.congregation_meeting_time.attributes.day_time.invalid_format",
                ),
              },
            ]}
          >
            <Input placeholder="hh:mm" style={{ width: 120 }} />
          </Form.Item>
        </Form>
      </Modal>

      {weeksOf && <WeeksDrawer meetingTime={weeksOf} onClose={() => setWeeksOf(null)} />}
    </Card>
  );
}

/**
 * The weeks a meeting time covers. Each row can take the time individually —
 * useful when a single week was moved and should not be reset by applying the
 * time to the whole period.
 */
function WeeksDrawer({
  meetingTime,
  onClose,
}: {
  meetingTime: MeetingTimeRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const weeks = useQuery({
    queryKey: ["congregation", "assignation_times", meetingTime.id],
    queryFn: () =>
      apiFetch<ListResponse<AssignationWeekRow>>(
        `/congregation/local_assignation_times/${meetingTime.id}`,
      ),
  });

  const applyToWeek = useMutation({
    mutationFn: (weekBegin: string) =>
      apiFetch<NotificationsResponse>(`/congregation/local_assignation_times/${weekBegin}`, {
        method: "PUT",
        body: { meeting_time_id: meetingTime.id },
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({
        queryKey: ["congregation", "assignation_times", meetingTime.id],
      });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const columns = useMemo<TableProps<AssignationWeekRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 200,
      },
      {
        title: t("activerecord.attributes.congregation_meeting_time.week_day"),
        dataIndex: "week_day_label",
        width: 140,
      },
      {
        title: t("activerecord.attributes.congregation_meeting_time.day_time"),
        dataIndex: "day_time",
        width: 100,
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        ellipsis: true,
      },
      {
        key: "actions",
        width: 160,
        render: (_value, row) =>
          // Only a week that has an event can take a time.
          row.has_event ? (
            <Button
              size="small"
              disabled={applyToWeek.isPending}
              onClick={() => applyToWeek.mutate(row.week_begin)}
            >
              {t("congregation_meeting_time.assign")}
            </Button>
          ) : null,
      },
    ],
    [t, applyToWeek],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={860}
      destroyOnHidden
      title={
        <Typography.Text>
          {[formatDate(meetingTime.valid_from), meetingTime.week_day_label, meetingTime.day_time]
            .filter(Boolean)
            .join(" · ")}
        </Typography.Text>
      }
    >
      <DataTable<AssignationWeekRow>
        columns={columns}
        rows={weeks.data?.data}
        exportAs={[
          t("activerecord.models.congregation_meeting_time"),
          formatDate(meetingTime.valid_from),
        ]
          .filter(Boolean)
          .join(" · ")}
        loading={weeks.isPending || applyToWeek.isPending}
        error={weeks.error}
        rowKey="week_begin"
        paginated={false}
      />
    </Drawer>
  );
}
